'use client';

import { Link } from 'react-router-dom';
import { subscribeNewsletter } from '@/lib/clientApi';
import { motion } from 'framer-motion';
import React, { Suspense } from 'react';
import { ArrowRight, Server, Cpu, HardDrive, Globe, Shield, Check, Database, Layers, Send, Clock } from 'lucide-react';

function VPSHostingContent() {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-36 pb-36 md:pb-36 min-h-[80vh] flex items-center -mt-2">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 z-[-1]"></div>
        
        {/* Server rack grid pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.05)_50%,transparent_100%)] bg-[length:4px_100%]"></div>
          <div className="absolute inset-0 bg-[linear-gradient(0deg,transparent_0%,rgba(255,255,255,0.05)_50%,transparent_100%)] bg-[length:100%_4px]"></div>
        </div>

        {/* Animated network connections */}
        <div className="absolute inset-0">
          <div className="absolute h-px w-full top-1/4 left-0 bg-gradient-to-r from-transparent via-primary-500/20 to-transparent animate-pulse"></div>
          <div className="absolute h-px w-full top-2/4 left-0 bg-gradient-to-r from-transparent via-primary-500/20 to-transparent animate-pulse delay-300"></div>
          <div className="absolute h-px w-full top-3/4 left-0 bg-gradient-to-r from-transparent via-primary-500/20 to-transparent animate-pulse delay-700"></div>
        </div>

        {/* Data flow lines */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-px h-full left-1/4 top-0 bg-gradient-to-b from-transparent via-primary-500/20 to-transparent animate-network-flow"></div>
          <div className="absolute w-px h-full left-2/4 top-0 bg-gradient-to-b from-transparent via-primary-500/20 to-transparent animate-network-flow delay-300"></div>
          <div className="absolute w-px h-full left-3/4 top-0 bg-gradient-to-b from-transparent via-primary-500/20 to-transparent animate-network-flow delay-700"></div>
        </div>

        {/* Server status indicators */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-[10%] flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-ping"></div>
            <div className="w-2 h-2 rounded-full bg-green-500 animate-ping delay-100"></div>
            <div className="w-2 h-2 rounded-full bg-green-500 animate-ping delay-200"></div>
          </div>
          <div className="absolute bottom-10 right-[10%] flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-ping"></div>
            <div className="w-2 h-2 rounded-full bg-green-500 animate-ping delay-100"></div>
            <div className="w-2 h-2 rounded-full bg-green-500 animate-ping delay-200"></div>
          </div>
        </div>

        {/* Server icons with glow */}
        <div className="absolute inset-0">
          <motion.div 
            className="absolute left-[5%] top-1/4 group"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="absolute inset-0 bg-primary-500/20 blur-xl rounded-full transform scale-150 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <Server className="relative text-primary-400 w-16 h-16 transform group-hover:scale-110 transition-transform" />
          </motion.div>
          <motion.div 
            className="absolute right-[5%] bottom-1/4 group"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="absolute inset-0 bg-primary-500/20 blur-xl rounded-full transform scale-150 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <HardDrive className="relative text-primary-400 w-12 h-12 transform group-hover:scale-110 transition-transform" />
          </motion.div>
          <motion.div 
            className="absolute right-[15%] top-1/3 group"
            animate={{ y: [0, -15, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="absolute inset-0 bg-primary-500/20 blur-xl rounded-full transform scale-150 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <Cpu className="relative text-primary-400 w-20 h-20 transform group-hover:scale-110 transition-transform" />
          </motion.div>
        </div>

        {/* Binary data particles */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full">
            {Array.from({ length: 20 }).map((_, i) => {
              const delayClasses = ['delay-0', 'delay-100', 'delay-200', 'delay-300'];
              const binaryValue = i % 2 === 0 ? '1' : '0';
              return (
                <div
                  key={i}
                  className={`absolute text-primary-500/10 text-xs animate-float-binary ${delayClasses[i % delayClasses.length]}`}
                  style={{
                    left: `${(i % 5) * 25}%`,
                    top: `${Math.floor(i / 5) * 25}%`
                  }}
                >
                  {binaryValue}
                </div>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              className="md:col-span-7"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <span className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-6">
                  Starting at just $4.99/month
                </span>
              </motion.div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white">
                <span className="text-transparent bg-clip-text bg-gradient-rainbow">VPS Hosting</span>
              </h1>
              
              <p className="text-xl mb-8 text-slate-300">
                Experience the power of dedicated resources with our high-performance VPS hosting solutions. Full root access, guaranteed resources, and instant scalability.
              </p>

              <div className="flex flex-col sm:flex-row gap-5">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="#pricing"
                    className="bg-gradient-rainbow text-white px-8 py-4 rounded-lg font-medium text-center transition-all shadow-neon inline-flex items-center justify-center"
                  >
                    View Plans <ArrowRight className="ml-2" />
                  </Link>
                </motion.div>
                
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/contact"
                    className="bg-slate-800/80 text-primary-400 hover:bg-slate-700 border border-slate-700/50 px-8 py-4 rounded-lg font-medium text-center transition-all shadow-md backdrop-blur-sm inline-flex items-center justify-center"
                  >
                    Contact Sales
                  </Link>
                </motion.div>
              </div>
            </motion.div>

            {/* Right Content - VPS Illustration */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden md:block md:col-span-5 mt-12 md:mt-12"
            >
              <div className="relative">
                {/* Glow effects */}
                <motion.div
                  className="absolute -top-10 -left-10 w-40 h-40 bg-gradient-to-br from-primary-500/20 to-tertiary-500/20 rounded-full blur-3xl"
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.5, 0.3]
                  }}
                  transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
                <motion.div
                  className="absolute -bottom-10 -right-10 w-60 h-60 bg-gradient-to-br from-secondary-500/20 to-accent-500/20 rounded-full blur-3xl"
                  animate={{ 
                    scale: [1, 1.3, 1],
                    opacity: [0.3, 0.6, 0.3]
                  }}
                  transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1
                  }}
                />

                {/* VPS Server Illustration */}
                <motion.div
                  className="bg-slate-800/90 p-8 rounded-2xl shadow-3d border border-slate-700/50 backdrop-blur-sm relative"
                  whileHover={{ y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Status Box - Top */}
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-slate-900/90 px-6 py-3 rounded-xl border border-slate-700/50 backdrop-blur-sm z-10 whitespace-nowrap">
                    <div className="flex items-center space-x-6">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                        <span className="text-xs text-slate-300">Hypervisor Active</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
                        <span className="text-xs text-slate-300">VMs Running</span>
                      </div>
                    </div>
                  </div>

                  {/* VPS Server Preview */}
                  <div className="relative w-full aspect-square bg-slate-900/50 rounded-xl overflow-hidden border border-slate-700/30">
                    <img
                      src="/assets/illustrations/vps-server.svg"
                      alt="VPS Server Illustration"
                      width={400}
                      height={400}
                      className="w-full h-full object-contain p-6"
                      fetchPriority="high"
                      decoding="async"
                    />

                    {/* Resource Meters */}
                    <div className="absolute bottom-6 left-6 right-6 flex space-x-4">
                      <div className="flex-1 bg-slate-800/90 rounded-lg p-3 backdrop-blur-sm border border-slate-700/50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-slate-300">CPU</span>
                          <motion.span 
                            className="text-xs text-primary-400"
                            animate={{ opacity: [1, 0.5, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                          >
                            Active
                          </motion.span>
                        </div>
                        <motion.div 
                          className="h-1.5 bg-gradient-rainbow rounded-full"
                          animate={{ 
                            scaleX: [0.3, 0.7, 0.3],
                          }}
                          transition={{ 
                            duration: 4,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                          style={{ originX: 0 }}
                        />
                      </div>
                      <div className="flex-1 bg-slate-800/90 rounded-lg p-3 backdrop-blur-sm border border-slate-700/50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-slate-300">RAM</span>
                          <motion.span 
                            className="text-xs text-primary-400"
                            animate={{ opacity: [1, 0.5, 1] }}
                            transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                          >
                            4GB
                          </motion.span>
                        </div>
                        <motion.div 
                          className="h-1.5 bg-gradient-rainbow rounded-full"
                          animate={{ 
                            scaleX: [0.4, 0.6, 0.4],
                          }}
                          transition={{ 
                            duration: 5,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                          style={{ originX: 0 }}
                        />
                      </div>
                      <div className="flex-1 bg-slate-800/90 rounded-lg p-3 backdrop-blur-sm border border-slate-700/50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-slate-300">SSD</span>
                          <motion.span 
                            className="text-xs text-primary-400"
                            animate={{ opacity: [1, 0.5, 1] }}
                            transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                          >
                            50GB
                          </motion.span>
                        </div>
                        <motion.div 
                          className="h-1.5 bg-gradient-rainbow rounded-full"
                          animate={{ 
                            scaleX: [0.5, 0.8, 0.5],
                          }}
                          transition={{ 
                            duration: 6,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                          style={{ originX: 0 }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Performance Badge */}
                  <motion.div 
                    className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-gradient-rainbow text-white px-6 py-3 rounded-xl shadow-neon"
                    animate={{ 
                      boxShadow: [
                        "0 0 10px rgba(59, 130, 246, 0.5)",
                        "0 0 20px rgba(59, 130, 246, 0.7)",
                        "0 0 10px rgba(59, 130, 246, 0.5)"
                      ]
                    }}
                    transition={{ 
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <div className="flex items-center space-x-2">
                      <Server className="h-6 w-6 mr-2" />
                      <span className="font-bold whitespace-nowrap">Full Root Access</span>
                    </div>
                  </motion.div>

                  {/* Network Lines Animation */}
                  <div className="absolute inset-0 pointer-events-none">
                    <motion.div
                      className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-500/20 to-transparent"
                      animate={{
                        y: ["0%", "100%", "0%"],
                        opacity: [0, 1, 0]
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "linear"
                      }}
                    />
                    <motion.div
                      className="absolute top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-primary-500/20 to-transparent"
                      animate={{
                        x: ["0%", "100%", "0%"],
                        opacity: [0, 1, 0]
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "linear",
                        delay: 1
                      }}
                    />
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-4"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Features
            </motion.span>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why Choose Our <span className="text-transparent bg-clip-text bg-gradient-rainbow">VPS Hosting</span>?
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Experience enterprise-grade VPS hosting with dedicated resources and full control.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Server className="h-6 w-6" />,
                title: "Dedicated Resources",
                description: "Guaranteed CPU, RAM, and storage resources exclusively for your VPS instance."
              },
              {
                icon: <Cpu className="h-6 w-6" />,
                title: "Full Root Access",
                description: "Complete control over your server with root access and choice of operating system."
              },
              {
                icon: <Database className="h-6 w-6" />,
                title: "NVMe Storage",
                description: "Lightning-fast NVMe SSD storage for superior performance and reliability."
              },
              {
                icon: <Layers className="h-6 w-6" />,
                title: "Instant Scaling",
                description: "Scale your resources up or down instantly as your needs change."
              },
              {
                icon: <Shield className="h-6 w-6" />,
                title: "DDoS Protection",
                description: "Advanced DDoS protection and security features to keep your VPS safe."
              },
              {
                icon: <Globe className="h-6 w-6" />,
                title: "Global Network",
                description: "Choose from multiple data center locations worldwide for optimal performance."
              }
            ].map((feature, index) => (
              <motion.div 
                key={feature.title}
                className="bg-slate-800/80 p-8 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-slate-700 group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
              >
                <div className="bg-gradient-to-br from-primary-500 to-primary-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-6 group-hover:bg-gradient-rainbow transition-all duration-500">
                  <div className="text-primary-500 group-hover:text-white transition-colors duration-300">
                    {feature.icon}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-slate-300">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-4"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Pricing Plans
            </motion.span>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              VPS <span className="text-transparent bg-clip-text bg-gradient-rainbow">Hosting Plans</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Choose the perfect VPS configuration for your needs.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                name: "1DC VPS 1",
                price: "$4.99",
                description: "Perfect for small applications",
                features: [
                  "2 vCPU Cores",
                  "4GB RAM",
                  "40GB NVMe SSD",
                  "Unmetered Bandwidth",
                  "1 IPv4 Address",
                  "Full Root Access",
                  "DDoS Protection",
                  "24/7 Support"
                ],
                popular: false
              },
              {
                name: "1DC VPS 2",
                price: "$8.99",
                description: "Great for growing applications",
                features: [
                  "4 vCPU Cores",
                  "8GB RAM",
                  "80GB NVMe SSD",
                  "Unmetered Bandwidth",
                  "1 IPv4 Address",
                  "Full Root Access",
                  "DDoS Protection",
                  "24/7 Support"
                ],
                popular: true
              },
              {
                name: "1DC VPS 3",
                price: "$18.99",
                description: "Ideal for business websites",
                features: [
                  "8 vCPU Cores",
                  "16GB RAM",
                  "160GB NVMe SSD",
                  "Unmetered Bandwidth",
                  "1 IPv4 Address",
                  "Full Root Access",
                  "DDoS Protection",
                  "24/7 Priority Support"
                ],
                popular: false
              },
              {
                name: "1DC VPS 4",
                price: "$36.99",
                description: "For high-traffic applications",
                features: [
                  "16 vCPU Cores",
                  "32GB RAM",
                  "320GB NVMe SSD",
                  "Unmetered Bandwidth",
                  "1 IPv4 Address",
                  "Full Root Access",
                  "DDoS Protection",
                  "24/7 Priority Support"
                ],
                popular: false
              }
            ].map((plan) => (
              <motion.div 
                key={plan.name}
                className={`bg-slate-800/80 rounded-xl shadow-xl border ${plan.popular ? 'border-primary-500 scale-105' : 'border-slate-700'} relative group backdrop-blur-sm overflow-hidden`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                whileHover={{ y: -10 }}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-gradient-rainbow text-white text-xs font-bold px-3 py-1 rounded-tr-xl rounded-bl-xl">
                    POPULAR
                  </div>
                )}
                <div className="p-6 border-b border-slate-700">
                  <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-rainbow">{plan.price}</span>
                    <span className="text-slate-400 ml-1">/month</span>
                  </div>
                  <p className="text-sm text-slate-400 mt-2">{plan.description}</p>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center">
                        <Check className="h-5 w-5 text-green-500 mr-2" />
                        <span className="text-slate-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <motion.div
                    className="mt-6"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Link
                      to={plan.name === "1DC VPS 1" ? "https://panel.1dollarcloud.com/order.php?step=1&productGroup=4&product=443" :
                           plan.name === "1DC VPS 2" ? "https://panel.1dollarcloud.com/order.php?step=1&productGroup=4&product=444" :
                           plan.name === "1DC VPS 3" ? "https://panel.1dollarcloud.com/order.php?step=1&productGroup=4&product=445" :
                           "https://panel.1dollarcloud.com/order.php?step=1&productGroup=4&product=446"}
                      className={`block w-full ${plan.popular ? 'bg-gradient-rainbow' : 'bg-slate-700'} text-white text-center py-3 px-4 rounded-lg transition-all shadow-lg`}
                    >
                      Get Started
                    </Link>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Worldwide Data Servers Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-4"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Global Infrastructure
            </motion.span>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-rainbow">Worldwide</span> Data Servers
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Experience lightning-fast loading times with our strategically positioned data centers across the globe.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="bg-slate-800/80 p-6 rounded-xl border border-slate-700/50 backdrop-blur-sm">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                  <Globe className="mr-3 h-6 w-6 text-primary-400" />
                  Global Network
                </h3>
                <p className="text-slate-300 mb-4">
                  Our 12 strategically located data centers ensure your website loads quickly for visitors anywhere in the world. With an average global latency of just 45ms, your users experience lightning-fast performance regardless of their location.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-slate-300">Redundant network connections with 99.99% uptime guarantee</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-slate-300">Automatic content distribution to the nearest data center</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-slate-300">Real-time traffic routing optimization for minimal latency</span>
                  </li>
                </ul>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700/50 backdrop-blur-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-white">North America</h4>
                    <span className="text-green-400 text-sm font-medium">~18ms</span>
                  </div>
                  <p className="text-slate-400 text-sm">3 data centers covering US East, US West, and Canada</p>
                </div>
                <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700/50 backdrop-blur-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-white">Europe</h4>
                    <span className="text-green-400 text-sm font-medium">~22ms</span>
                  </div>
                  <p className="text-slate-400 text-sm">4 data centers in UK, Germany, France, and Netherlands</p>
                </div>
                <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700/50 backdrop-blur-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-white">Asia Pacific</h4>
                    <span className="text-green-400 text-sm font-medium">~35ms</span>
                  </div>
                  <p className="text-slate-400 text-sm">3 data centers in Singapore, Japan, and Australia</p>
                </div>
                <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700/50 backdrop-blur-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-white">Other Regions</h4>
                    <span className="text-green-400 text-sm font-medium">~40ms</span>
                  </div>
                  <p className="text-slate-400 text-sm">2 data centers in Brazil and South Africa</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-[#141e30] p-6 rounded-xl border border-slate-700/50 backdrop-blur-sm relative overflow-hidden">
                {/* World Map */}
                <div className="relative h-[400px] w-full">
                  <div className="absolute inset-0">
          <img
                      src="/images/world-map.png"
                      alt="World Map"
                      width={1000}
                      height={500}
                      className="w-full h-full object-contain"
                    />
                    
                    {/* Server Locations */}
                    <div className="absolute group" style={{ top: 'calc(50% - 12px)', left: 'calc(17% - 9px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30"></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        US West
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(50% - 12px)', left: 'calc(28% - 10px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.7s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        US East
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(38% - 12px)', left: 'calc(54% - 18px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.3s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        UK
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(36% - 12px)', left: 'calc(20% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.9s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        Canada
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(40% - 12px)', left: 'calc(60% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.2s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        Germany
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(30% - 12px)', left: 'calc(75% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.6s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        Russia
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(60% - 12px)', left: 'calc(69% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.4s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        India
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(65% - 12px)', left: 'calc(75% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.8s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        Singapore
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(50% - 12px)', left: 'calc(86% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.1s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        Japan
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(74% - 12px)', left: 'calc(84% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.5s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        Australia
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(65% - 12px)', left: 'calc(33% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.7s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        Brazil
                      </div>
                    </div>
                    
                    <div className="absolute group" style={{ top: 'calc(77% - 12px)', left: 'calc(55% - 12px)' }}>
                      <div className="w-6 h-6 flex flex-col items-center">
                        <div className="flex space-x-1 mb-0.5">
                          <div className="w-1 h-1 rounded-full bg-green-400 shadow-[0_0_2px_#4ade80] border border-green-200/30"></div>
                          <div className="w-1 h-1 rounded-full">
                            <div className="w-full h-full bg-red-500 rounded-full animate-[opacity-pulse_1s_ease-in-out_infinite] shadow-[0_0_2px_#ef4444] border border-red-200/30" style={{ animationDelay: '0.3s' }}></div>
                          </div>
                        </div>
                        <Server className="text-primary-400 h-4 w-4" />
                      </div>
                      <div className="absolute left-0 -top-8 bg-slate-800/90 text-xs text-white whitespace-nowrap py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-slate-700/50 z-10">
                        South Africa
                      </div>
                    </div>
                  </div>
                  
                  {/* Latency indicators */}
                  <div className="absolute bottom-4 right-4 bg-slate-900/80 p-3 rounded-lg border border-slate-700/50 backdrop-blur-sm">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-green-400"></div>
                      <span className="text-xs text-slate-300">Low Latency (&lt;30ms)</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                      <span className="text-xs text-slate-300">Medium Latency (30-60ms)</span>
                    </div>
                  </div>
                </div>
                
                {/* Overlay glow effects */}
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-primary-500/10 to-tertiary-500/10 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-br from-secondary-500/10 to-primary-500/10 rounded-full blur-3xl"></div>
              </div>
              
              <div className="absolute -bottom-5 -right-5 bg-gradient-rainbow text-white p-4 rounded-xl shadow-neon">
                <div className="flex items-center">
                  <Clock className="h-6 w-6 mr-2" />
                  <span className="font-bold">45ms Avg. Global Latency</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-4"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Why Choose Us
            </motion.span>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why <span className="text-transparent bg-clip-text bg-gradient-rainbow">1 Dollar Cloud</span> VPS
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Experience enterprise-grade VPS hosting at an unbeatable price point
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "High Performance",
                description: "Latest generation AMD EPYC processors and NVMe SSDs ensure blazing-fast performance for your applications.",
                icon: <Cpu className="h-6 w-6" />,
                benefits: [
                  "AMD EPYC Processors",
                  "NVMe SSD Storage",
                  "High I/O Performance",
                  "Low Latency Network"
                ]
              },
              {
                title: "Complete Control",
                description: "Full root access and choice of operating system gives you complete control over your server environment.",
                icon: <Server className="h-6 w-6" />,
                benefits: [
                  "Root Access",
                  "Custom ISO Support",
                  "API Access",
                  "Multiple OS Options"
                ]
              },
              {
                title: "Instant Deployment",
                description: "Deploy your VPS instantly and scale resources up or down with zero downtime.",
                icon: <Layers className="h-6 w-6" />,
                benefits: [
                  "Instant Provisioning",
                  "Easy Scaling",
                  "Resource Management",
                  "Snapshot Support"
                ]
              },
              {
                title: "Network Security",
                description: "Enterprise-grade security features protect your VPS from threats and attacks.",
                icon: <Shield className="h-6 w-6" />,
                benefits: [
                  "DDoS Protection",
                  "Firewall Rules",
                  "Private Networking",
                  "SSL Certificates"
                ]
              },
              {
                title: "Global Infrastructure",
                description: "Choose from multiple data center locations for optimal performance worldwide.",
                icon: <Globe className="h-6 w-6" />,
                benefits: [
                  "Multiple Locations",
                  "Tier-4 Data Centers",
                  "Redundant Network",
                  "99.99% Uptime"
                ]
              },
              {
                title: "24/7 Support",
                description: "Expert support team available 24/7 to help you with any issues or questions.",
                icon: <Server className="h-6 w-6" />,
                benefits: [
                  "Technical Support",
                  "Server Management",
                  "Migration Assistance",
                  "Knowledge Base"
                ]
              }
            ].map((benefit, index) => (
              <motion.div
                key={benefit.title}
                className="bg-slate-800/80 rounded-xl p-6 border border-slate-700/50 backdrop-blur-sm group hover:border-primary-500/50 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
              >
                <div className="bg-gradient-to-br from-primary-500 to-primary-100 p-3 rounded-lg w-12 h-12 flex items-center justify-center mb-6 group-hover:bg-gradient-rainbow transition-all duration-500">
                  <div className="text-primary-500 group-hover:text-white transition-colors duration-300">
                    {benefit.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
                <p className="text-slate-300 mb-4">{benefit.description}</p>
                <ul className="space-y-2">
                  {benefit.benefits.map((item, i) => (
                    <li key={i} className="flex items-center text-sm group/item">
                      <div className="h-4 w-4 mr-2 flex-shrink-0 flex items-center justify-center">
                        <Check className="text-green-500 group-hover/item:scale-110 transition-transform" />
                      </div>
                      <span className="text-slate-300 group-hover/item:text-slate-200 transition-colors">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Background elements - Data center visualization */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        
        {/* Server visualization background */}
        <div className="absolute inset-0">
          {/* Connection lines */}
          <div className="absolute inset-0">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={`line-${i}`}
                className="absolute bg-primary-600/10 h-[1px]"
                style={{
                  top: `${5 + i * 5}%`,
                  left: '0',
                  right: '0',
                }}
                animate={{
                  opacity: [0.1, 0.3, 0.1],
                }}
                transition={{
                  duration: 3 + (i % 5),
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.1,
                }}
              />
            ))}
            
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={`vline-${i}`}
                className="absolute bg-primary-600/10 w-[1px]"
                style={{
                  left: `${5 + i * 5}%`,
                  top: '0',
                  bottom: '0',
                }}
                animate={{
                  opacity: [0.1, 0.2, 0.1],
                }}
                transition={{
                  duration: 4 + (i % 3),
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.2,
                }}
              />
            ))}
          </div>
          
          {/* Data nodes */}
          <div className="absolute inset-0">
            {[...Array(30)].map((_, i) => {
              const size = 2 + (i % 3) * 2;
              return (
                <motion.div
                  key={`node-${i}`}
                  className="absolute rounded-full bg-primary-500/30"
                  style={{
                    width: size,
                    height: size,
                    top: `${10 + (i * 73) % 80}%`,
                    left: `${5 + (i * 57) % 90}%`,
                  }}
                  animate={{
                    opacity: [0.2, 0.5, 0.2],
                    scale: [1, 1.5, 1],
                  }}
                  transition={{
                    duration: 3 + (i % 4),
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 0.1,
                  }}
                />
              );
            })}
          </div>
          
          {/* Data flow animations */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(15)].map((_, i) => {
              const isVertical = i % 2 === 0;
              const size = isVertical ? '1px' : '2px';
              const startPosition = `${10 + (i * 37) % 80}%`;
              
              return (
                <motion.div
                  key={`flow-${i}`}
                  className="absolute bg-gradient-to-r from-primary-500/0 via-primary-500/50 to-primary-500/0"
                  style={{
                    width: isVertical ? size : '100px',
                    height: isVertical ? '50px' : size,
                    top: isVertical ? '0' : startPosition,
                    left: isVertical ? startPosition : '0',
                  }}
                  animate={{
                    [isVertical ? 'top' : 'left']: ['0%', '100%'],
                    opacity: [0, 1, 0],
                  }}
                  transition={{
                    duration: 4 + (i % 3) * 2,
                    repeat: Infinity,
                    ease: "linear",
                    delay: i * 0.2,
                  }}
                />
              );
            })}
          </div>
          
          {/* Subtle grid overlay */}
          <div className="absolute inset-0 bg-[url('/assets/grid-pattern.svg')] bg-[length:40px_40px] opacity-[0.04]"></div>
          
          {/* Subtle noise texture */}
          <div className="absolute inset-0 bg-[url('/assets/noise.svg')] opacity-[0.02] mix-blend-overlay"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <span className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-6">
                Get Started Today
              </span>
              
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white">
                Ready to <span className="text-transparent bg-clip-text bg-gradient-rainbow">Get Started</span>?
              </h2>
              
              <p className="text-xl mb-8 text-slate-300">
                Choose the perfect hosting plan for your WordPress site and get online today.
              </p>
              
              <motion.div
                className="inline-block"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/hosting"
                  className="bg-gradient-rainbow text-white px-8 py-4 rounded-lg font-medium text-center transition-all shadow-neon inline-flex items-center justify-center"
                >
                  Get Started Now <ArrowRight className="ml-2" />
                </Link>
              </motion.div>
              
              <motion.div
                className="mt-12 max-w-md mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <h4 className="text-xl font-semibold text-white mb-4">Subscribe to our newsletter</h4>
                <form onSubmit={async (e) => {
                   e.preventDefault();
                   const form = e.target as HTMLFormElement;
                   const email = (form.elements.namedItem('email') as HTMLInputElement).value;
                   let message = '';
                   let color = '';
                   if (email) {
                     const result = await subscribeNewsletter(email);
                     if (result.ok) {
                       message = result.message;
                        color = 'green';
                        (form.elements.namedItem('email') as HTMLInputElement).value = ''; // Clear the input field
                      } else {
                        message = result.message;
                        color = 'red';
                      }
                    } else {
                      message = 'Please enter your email address.';
                      color = 'red';
                    }
                    const subscribeMessage = document.getElementById('subscribe-message');
                    if (subscribeMessage) {
                      subscribeMessage.textContent = message;
                      subscribeMessage.style.color = color;
                    }
                  }}>
                   <div className="flex flex-col sm:flex-row">
                     <input
                       type="email"
                      name="email"
                      placeholder="Your email"
                      className="bg-slate-800/80 text-white rounded-l-lg sm:rounded-r-none rounded-r-lg sm:rounded-b-none rounded-b-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 w-full border border-slate-700 mb-2 sm:mb-0"
                    />
                    <motion.button
                      type="submit"
                      className="bg-gradient-rainbow text-white px-6 py-3 rounded-l-lg sm:rounded-l-none rounded-r-lg font-medium flex items-center justify-center"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Send className="mr-2" />
                      <span>Subscribe</span>
                    </motion.button>
                  </div>
                  <p className="mt-3 text-xs text-slate-400">
                    By subscribing, you agree to our Privacy Policy and consent to receive updates.
                  </p>
                  <p id="subscribe-message" className="mt-2 text-base text-center"></p>
                </form>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default function VPSHostingPage() {
  return (
    <VPSHostingContent />
  );
}